import React from "react";
import { Link, useParams } from "react-router-dom";
import Header from "components/ui/Header";
import Footer from "components/Footer";
import SEO from "components/SEO";
import NotFound from "pages/NotFound";
import { companyProfilePages } from "data/profilesData";

const companyProfiles = Object.values(companyProfilePages);

function SectionTitle({ children }) {
  return (
    <div className="mb-4">
      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight leading-tight">{children}</h2>
      <div className="mt-2 h-0.5 w-14 rounded-full bg-[#00d690]" aria-hidden />
    </div>
  );
}

const CompanyProfilePage = () => {
  const { profileSlug } = useParams();
  const slug = profileSlug ?? "company-overview";
  const profile = companyProfiles.find((entry) => entry.slug === slug);

  if (!profile) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-[#f4faf8] overflow-x-hidden">
      <SEO
        title={profile.seoTitle ?? `${profile.title} | OctalChip`}
        description={profile.intro}
        keywords="octalchip company profile, AI engineering company profile, generative AI partner, Cloud AI & LLMOps company"
        canonical={`https://www.octalchip.com/company-profile/${profile.slug}`}
      />
      <Header />
      <main className="py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-5xl">
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 mb-6">
            <p className="text-sm font-semibold tracking-wide text-[#00d690] uppercase mb-3">OctalChip</p>
            <div className="mb-4 h-0.5 w-16 rounded-full bg-[#00d690]" aria-hidden />
            <h1 className="text-3xl sm:text-[2.125rem] sm:leading-tight font-bold text-gray-900 mb-3">
              {profile.heroHeading ?? profile.title}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg mb-5 font-medium">{profile.subtitle}</p>
            <p className="text-gray-700 leading-relaxed">{profile.intro}</p>
            <p className="mt-4">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Suggested for</span>
              <span className="block text-sm sm:text-base text-gray-600 mt-1">{profile.fitFor}</span>
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="https://cal.com/octalchip/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-full bg-[#00d690] text-white font-medium hover:bg-[#00b876] transition-colors"
              >
                Schedule a discovery call
              </a>
              <Link
                to="/contact"
                className="px-5 py-2.5 rounded-full border border-[#00d690] text-[#00d690] font-medium hover:bg-[#00d690] hover:text-white transition-colors"
              >
                Request a proposal
              </Link>
            </div>
          </section>

          <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {profile.stats.map((item) => (
              <article key={item.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <p className="text-2xl font-bold text-[#00d690]">{item.value}</p>
                <p className="text-xs text-gray-600 mt-1">{item.label}</p>
              </article>
            ))}
          </section>

          {profile.sections.map((section) => (
            <section key={section.heading} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
              <SectionTitle>{section.heading}</SectionTitle>
              <ul className="space-y-3 list-disc pl-5">
                {section.bullets.map((item) => (
                  <li key={item} className="text-gray-700 leading-relaxed">{item}</li>
                ))}
              </ul>
            </section>
          ))}

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <SectionTitle>How we partner with you</SectionTitle>
            <div className="grid md:grid-cols-2 gap-3">
              {profile.process.map((step, index) => (
                <div key={step} className="rounded-xl bg-[#f4faf8] border border-[#dbf4e8] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#00d690] mb-1">
                    Phase {index + 1}
                  </p>
                  <p className="text-gray-700">{step}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid md:grid-cols-2 gap-6 mb-6">
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <SectionTitle>What success looks like</SectionTitle>
              <ul className="space-y-3 list-disc pl-5">
                {profile.outcomes.map((item) => (
                  <li key={item} className="text-gray-700 leading-relaxed">{item}</li>
                ))}
              </ul>
            </article>
            <article className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <SectionTitle>Security & compliance</SectionTitle>
              <ul className="space-y-3 list-disc pl-5">
                {profile.security.map((item) => (
                  <li key={item} className="text-gray-700 leading-relaxed">{item}</li>
                ))}
              </ul>
            </article>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <SectionTitle>Sectors we support</SectionTitle>
            <div className="flex flex-wrap gap-2">
              {profile.industries.map((industry) => (
                <span key={industry} className="px-3 py-1.5 text-sm bg-[#f4faf8] text-gray-700 border border-[#dbf4e8] rounded-full">
                  {industry}
                </span>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <SectionTitle>Ways to work together</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-3">
              {profile.engagementModels.map((model) => (
                <div key={model} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-gray-700 text-sm sm:text-base">
                  {model}
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <SectionTitle>Other profiles</SectionTitle>
            <p className="text-sm text-gray-600 mb-5 max-w-2xl -mt-1">
              Pick the view that fits how your team evaluates partners.
            </p>
            <div className="flex flex-wrap gap-3">
              {companyProfiles
                .filter((entry) => entry.slug !== profile.slug)
                .map((entry) => (
                  <Link
                    key={entry.slug}
                    to={`/company-profile/${entry.slug}`}
                    title={entry.subtitle}
                    className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#00d690] text-[#00d690] bg-white hover:bg-[#00d690] hover:text-white transition-all duration-200 text-sm sm:text-base font-semibold shadow-sm hover:shadow-md"
                  >
                    <span>{entry.clientNavLabel}</span>
                    <span className="text-lg leading-none transition-transform group-hover:translate-x-0.5" aria-hidden>
                      →
                    </span>
                  </Link>
                ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CompanyProfilePage;
